import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { IDataboxAttemptsIcon } from 'src/app/api/modules/core/dynamic/databoxes/attempts-icon/IDataboxAttemptsIcon';
import { GenerateFaIcon } from 'src/app/factories/generators/icon-fa.generator';
import { GenerateIconState } from 'src/app/factories/generators/icon-state.generator';
import { IconStateComponent } from '../../../../../icons/components/icon-state/icon-state.component';

/**
 * The icon state set used when the configuration does not provide one: an
 * empty circle for `default`, a filled check for `positive` and a filled
 * cross for `negative`.
 */
function defaultState() {
  return GenerateIconState({
    states: [
      GenerateFaIcon({ name: 'far:circle', state: 'default' }),
      GenerateFaIcon({ name: 'fas:circle-check', state: 'positive' }),
      GenerateFaIcon({ name: 'fas:circle-xmark', state: 'negative' }),
    ],
  });
}

/**
 * Displays the user's attempts as a row of icons inside a DataboxContainer
 * (e.g. "n p o o o" for a quiz answered wrong then right): one icon per entry
 * of the state-name list pushed in through {@link update}, each showing the
 * icon whose state matches its name.
 *
 * An unconfigured databox renders nothing until a state-name list arrives
 * (from `startingState` or an update).
 *
 * See {@link IDataboxAttemptsIcon} for the configuration structure.
 */
@Component({
  selector: 'app-databox-attempts-icon',
  templateUrl: './databox-attempts-icon.component.html',
  styleUrl: './databox-attempts-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconStateComponent],
})
export class DataboxAttemptsIconComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources; the built-in defaults
   * are used until a usable config arrives.
   */
  readonly config = input<IDataboxAttemptsIcon | null>();

  /**
   * The last valid state-name list received via {@link update}, or
   * `undefined` while the configured `startingState` applies.
   */
  private readonly stateList = signal<string[] | undefined>(undefined);

  /**
   * The icon state set to display, with warnings for configuration problems
   * (logged by an effect). A provided state set is accepted even when it
   * lacks a `default` entry — IconState falls back gracefully — so the
   * warnings are advisory only.
   */
  private readonly viewState = computed(() => {
    const configuration = this.config();
    const warnings: string[] = [];

    let activeState = defaultState();
    const configuredStates = configuration?.state?.states;
    if (configuredStates) {
      if (!configuredStates.some((icon) => icon.state === 'default')) {
        warnings.push(`You must supply a state with the name of "default" to one of your icons.`);
      }
      if (configuredStates.length === 1) {
        warnings.push(`You only have provided one state... kind of useless`);
      }
      activeState = configuration.state;
    }

    return { activeState, warnings };
  });

  /**
   * One entry per icon to display: the shared icon state set plus the state
   * name selecting which icon shows.
   */
  protected readonly states = computed(() => {
    const { activeState } = this.viewState();
    const names = this.stateList() ?? this.config()?.startingState ?? [];

    return names.map((stateName) => ({ state: activeState, stateName }));
  });

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      for (const warning of this.viewState().warnings) {
        this.loggerService.warn(`[DataboxAttemptsIcon] - ${warning}`);
      }
    });

    // a new config returns the databox to its configured starting state
    effect(() => {
      this.config();
      untracked(() => this.stateList.set(undefined));
    });
  }

  /**
   * Updates the component with the user's attempts.
   *
   * A missing list is ignored and the current display is kept.
   *
   * @param stateList - the state name to show for each attempt, in order
   */
  update(stateList: string[]) {
    if (!stateList) {
      return;
    }
    // copy on write: callers may mutate and re-send the same array, which
    // reference-comparing signals would otherwise treat as "unchanged"
    this.stateList.set([...stateList]);
  }
}
